import Link from "next/link";
import { Container } from "@/components/molecules/Container";
import { Inner } from "@/components/molecules/Inner";
import Contact from "@/components/organisms/Contact";
import Careers from "@/components/organisms/Careers";
import { Footer } from "@/components/templates/Footer";
import { Header } from "@/components/templates/Header";
import Layout from "@/components/templates/Layout";
import Meta from "@/components/templates/Meta";
import { useBudouX } from "@/hooks/useBudouX";
import ScrollDepthTracker from "@/components/utils/ScrollDepthTracker";

export default function WebinarAiAgentAutonomy() {
  const isHeaderRelative = true;
  const { parse } = useBudouX();
  return (
    <>
      <Meta
        pageTitle="AIエージェント自律化のリアル | ウェビナー"
        pageDescription="生成AIを「都度指示して使う」時代が終わりつつある。自律的に計画・実行するAIエージェントを社内でどう設計しどう安全に動かすか、トッカが完全AI駆動開発で得た実践知を60分に凝縮。"
        pagePath="https://tocca.systems/event/webinar/ai-agent-autonomy/"
        pageImg="https://tocca.systems/webinar-ai-agent-autonomy.png"
      />
      <Layout>
        <Header isRelative={isHeaderRelative} />
        <main className="z-10 flex-1">
          <ScrollDepthTracker label="ai-agent-autonomy" />
          <Container>
            <Inner className="max-w-full">
              {/* パンくずリスト */}
              <nav className="mx-auto max-w-4xl px-6 pb-4 pt-6 sm:px-8 md:px-12 xl:px-0">
                <ol className="flex flex-wrap items-center gap-1 text-sm text-txt-light">
                  <li>
                    <Link href="/event/"><a className="hover:text-pri hover:underline">
                      イベント
                    </a></Link>
                  </li>
                  <li className="mx-1">&gt;</li>
                  <li>
                    <Link href="/event/#webinar"><a className="hover:text-pri hover:underline">
                      ウェビナー
                    </a></Link>
                  </li>
                  <li className="mx-1">&gt;</li>
                  <li className="text-txt-base">AIエージェント自律化のリアル</li>
                </ol>
              </nav>

              {/* メインコンテンツ */}
              <article className="mx-auto max-w-4xl px-6 pb-16 sm:px-8 md:px-12 xl:px-0">
                {/* 終了バッジ */}
                <div className="mb-4 text-center">
                  <span className="inline-block rounded bg-txt-light px-4 py-1 text-sm font-bold text-white">
                    このウェビナーは終了しました
                  </span>
                </div>

                {/* タイトル */}
                <h1 className="mb-6 text-center font-bold leading-comfort text-txt-base fluid-text-[22px,32px]">
                  {parse("AIエージェント自律化のリアル ― 「指示待ちAI」から「24時間動くAI」へ、稼働量を4.75倍にした実践知")}
                </h1>

                {/* バナー画像 */}
                <div className="mb-10">
                  <img
                    src="/webinar-ai-agent-autonomy.png"
                    alt="AIエージェント自律化のリアル ウェビナー（終了）"
                    className="w-full rounded-lg shadow-md"
                  />
                </div>

                {/* ウェビナー概要 */}
                <section className="mb-10">
                  <h2 className="mb-1 font-bold text-pri fluid-text-[18px,24px]">
                    ウェビナー概要
                  </h2>
                  <div className="mb-6 h-1 w-16 bg-pri" />
                  <div className="space-y-4 leading-comfort text-txt-base fluid-text-[14px,16px]">
                    <p>
                      {parse("生成AIを「都度指示して使う」時代が終わりつつあります。自律的に計画・実行するAIエージェントを社内でどう設計し、どう安全に動かすか——トッカが完全AI駆動開発で得た実践知を60分に凝縮してお届けします。")}
                    </p>
                    <p>
                      {parse("稼働量4.75倍、72万ステップの開発実績をもとに、リアルな導入プロセスをお話しします。")}
                    </p>
                  </div>
                </section>

                {/* こんな方におすすめ */}
                <section className="mb-10">
                  <h2 className="mb-1 font-bold text-pri fluid-text-[18px,24px]">
                    こんな方におすすめです！
                  </h2>
                  <div className="mb-6 h-1 w-16 bg-pri" />
                  <ul className="space-y-3 text-txt-base fluid-text-[14px,16px]">
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5 shrink-0 text-pri">・</span>
                      <span>AIエージェントの都度指示に疲れた方</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5 shrink-0 text-pri">・</span>
                      <span>Claude Code / Codex / Gemini CLI を入れたが稼働量が伸びない方</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5 shrink-0 text-pri">・</span>
                      <span>自律運用したいがコスト・セキュリティが不安な方</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5 shrink-0 text-pri">・</span>
                      <span>他社事例を知りたい経営層・技術責任者</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5 shrink-0 text-pri">・</span>
                      <span>AI駆動開発の次の一歩を探す開発組織リーダー</span>
                    </li>
                  </ul>
                </section>

                {/* 内容 */}
                <section className="mb-10">
                  <h2 className="mb-1 font-bold text-pri fluid-text-[18px,24px]">
                    お話しする内容
                  </h2>
                  <div className="mb-6 h-1 w-16 bg-pri" />
                  <ul className="space-y-3 text-txt-base fluid-text-[14px,16px]">
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5 shrink-0 text-pri">&#x2714;</span>
                      <span>AIエージェント自律化とは — 都度指示型と自律型の違い、稼働量4.75倍の仕組み</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5 shrink-0 text-pri">&#x2714;</span>
                      <span>完全AI駆動開発で72万ステップの実践事例</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5 shrink-0 text-pri">&#x2714;</span>
                      <span>自律化を支える環境設計 — コスト制御・権限管理・セキュリティ対策</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5 shrink-0 text-pri">&#x2714;</span>
                      <span>自律化への道のりと次に来るもの — 試行→幻滅→改善→自律化の実体験</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5 shrink-0 text-pri">&#x2714;</span>
                      <span>質疑応答</span>
                    </li>
                  </ul>
                </section>

                {/* 登壇者 */}
                <section className="mb-10">
                  <h2 className="mb-1 font-bold text-pri fluid-text-[18px,24px]">
                    登壇者
                  </h2>
                  <div className="mb-6 h-1 w-16 bg-pri" />
                  <div className="flex items-center gap-6 rounded-xl bg-bgc-base p-6">
                    <img
                      src="/photo-aboutGreeting.jpg"
                      alt="谷 樹"
                      className="h-24 w-24 shrink-0 rounded-full object-cover shadow-md"
                      style={{ objectPosition: "50% 0%" }}
                    />
                    <div>
                      <p className="mb-1 text-sm text-txt-light">株式会社トッカシステムズ 代表取締役</p>
                      <p className="mb-2 font-bold text-txt-base fluid-text-[18px,22px]">
                        谷 樹
                      </p>
                      <p className="text-sm text-txt-light">
                        完全AI駆動開発で得た自律化の実践知をお伝えします。
                      </p>
                    </div>
                  </div>
                </section>

                {/* 開催情報 */}
                <section className="mb-10">
                  <h2 className="mb-1 font-bold text-pri fluid-text-[18px,24px]">
                    開催情報
                  </h2>
                  <div className="mb-6 h-1 w-16 bg-pri" />
                  <div className="rounded-xl border border-bdc-light bg-white p-6">
                    <dl className="grid gap-4 sm:grid-cols-[auto_1fr]">
                      <dt className="font-bold text-pri">日時</dt>
                      <dd className="text-txt-base">8月4日（火）18:30〜19:30 <span className="font-bold text-ter">※終了</span></dd>
                      <dt className="font-bold text-pri">参加費</dt>
                      <dd className="text-txt-base">無料</dd>
                      <dt className="font-bold text-pri">開催形式</dt>
                      <dd className="text-txt-base">オンライン（Zoom）</dd>
                      <dt className="font-bold text-pri">主催</dt>
                      <dd className="text-txt-base">株式会社トッカシステムズ</dd>
                    </dl>
                  </div>
                </section>

                {/* アーカイブ配信申請CTA */}
                <section className="mb-10">
                  <h2 className="mb-1 font-bold text-pri fluid-text-[18px,24px]">
                    アーカイブ配信
                  </h2>
                  <div className="mb-6 h-1 w-16 bg-pri" />
                  <p className="mb-6 leading-comfort text-txt-base fluid-text-[13px,15px]">
                    本ウェビナーは終了しました。録画でのご視聴をご希望の方は、以下のフォームよりアーカイブ配信をお申し込みください。
                  </p>
                  <div className="text-center">
                    <a
                      href="https://forms.cloud.microsoft/Pages/ResponsePage.aspx?id=fwHoS0CTlky73SDON9vQOjHROJY6nrpPiAOxd7PIvTNUMTBYVUhZM0pIS0UzR1c0NE04OVA2R0tTWSQlQCN0PWcu"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-md border-2 border-pri px-10 py-4 font-bold text-pri transition-all hover:bg-pri hover:text-white fluid-text-[14px,16px]"
                    >
                      ＞ アーカイブ配信を申し込む
                    </a>
                  </div>
                </section>

                {/* ウェビナー一覧に戻る */}
                <div className="text-center">
                  <Link href="/event/">
                    <a className="inline-flex items-center justify-center rounded-md bg-pri px-10 py-4 font-bold text-white transition-all hover:bg-hov fluid-text-[14px,16px]">
                    ＞ ウェビナーTOPへ戻る
                    </a>
                  </Link>
                </div>
              </article>
            </Inner>
          </Container>
        </main>
        <Contact>
          システム構築のご相談やご依頼など、お悩みのことがあれば、お気軽にお問い合わせください。
        </Contact>
        <Careers />
        <Footer />
      </Layout>
    </>
  );
}
